import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import logger from './utils/logger';

const MyAccount = () => {
  const navigate = useNavigate();
  const { handleApiError, userData: contextUserData, setUserData: setContextUserData } = useContext(AuthContext);
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    role: '',
    teacher_email: '',
    usage_remaining: 0,
    created_at: '',
    grammar_exemption: false,
    teacher_code: null,
    referral_code: null
  });
  const [isEditing, setIsEditing] = useState({});
  const [subjectTeachers, setSubjectTeachers] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSelectingRole, setIsSelectingRole] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [teacherInputMode, setTeacherInputMode] = useState({});  // 'email' or 'code'
  const [teacherCode, setTeacherCode] = useState({});  // Store teacher codes by subject
  const [isCopied, setIsCopied] = useState(false); // For copy feedback
  
  // List of available subjects
  const subjects = ["English", "Irish", "French", "Spanish", "German", "Maths"];

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const rawToken = localStorage.getItem('token') || '';
        const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/profile`,
          {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Authorization': authToken,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Server error: ${await response.text()}`);
        }

        const data = await response.json();
        setUserData(data);
        
        // Initialize subject teachers from the response
        if (data.teacher_email) {
          try {
            // Check if it's already in JSON format
            if (data.teacher_email.startsWith('{')) {
              const teacherDict = JSON.parse(data.teacher_email);
              setSubjectTeachers(teacherDict);
            } else {
              // Legacy format - single email for all subjects
              setSubjectTeachers({ "English": data.teacher_email });
            }
          } catch (e) {
            logger.error('Error parsing teacher emails:', e);
            // Default to empty if there's an error
            setSubjectTeachers({});
          }
        }
      } catch (error) {
        handleApiError(error);
        setError('Failed to load user data');
      }
    };

    fetchUserData();
  }, [handleApiError]);

  // Fetch students if user is a teacher
  useEffect(() => {
    const fetchStudents = async () => {
      if (userData.role !== 'teacher') return;

      try {
        const rawToken = localStorage.getItem('token') || '';
        const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/students`,
          {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Authorization': authToken,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Server error: ${await response.text()}`);
        }

        const data = await response.json();
        setStudents(data.students_by_subject);
        
        // Set the initial selected subject to the one with the most students
        if (data.students_by_subject) {
          // Find subjects with students and sort by count (descending)
          const subjectsWithStudents = Object.keys(data.students_by_subject)
            .filter(subject => data.students_by_subject[subject].length > 0)
            .sort((a, b) => {
              const countA = data.students_by_subject[a].length;
              const countB = data.students_by_subject[b].length;
              return countB - countA; // Sort descending
            });
          
          if (subjectsWithStudents.length > 0) {
            // Select the subject with the most students
            setSelectedSubject(subjectsWithStudents[0]);
          } else {
            // Default to English if no students found
            setSelectedSubject("English");
          }
        }
      } catch (error) {
        logger.error('Error fetching students:', error);
        setError('Failed to load students');
      }
    };

    fetchStudents();
  }, [userData.role]);

  // Handle grammar exemption toggle
  const handleGrammarExemptionToggle = async () => {
    try {
      const rawToken = localStorage.getItem('token') || '';
      const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

      const newValue = !userData.grammar_exemption;
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/update-preferences`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Authorization': authToken,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ grammar_exemption: newValue })
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${await response.text()}`);
      }

      const data = await response.json();
      
      // Update local state
      setUserData({ ...userData, grammar_exemption: newValue });
      
      // Update context state if available
      if (setContextUserData && contextUserData) {
        setContextUserData({ ...contextUserData, grammar_exemption: newValue });
      }
      
      setSuccess('Grammar exemption setting updated');
      setError(null);
    } catch (error) {
      logger.error('Error updating grammar exemption:', error);
      setError(error.message || 'Failed to update grammar exemption setting');
    }
  };

  // Helper function to enable editing for a specific subject
  const handleEditTeacher = (subject) => {
    setIsEditing(prev => ({
      ...prev,
      [subject]: true
    }));
  };
  
  // Helper function to cancel editing for a specific subject
  const cancelEditTeacher = (subject) => {
    setIsEditing(prev => ({
      ...prev,
      [subject]: false
    }));
  };
  
  // Helper function to update teacher email for a specific subject
  const updateSubjectTeacher = (subject, email) => {
    setSubjectTeachers(prev => ({
      ...prev,
      [subject]: email
    }));
  };
  
  // Helper function to get teacher email for a specific subject
  const getTeacherForSubject = (subject) => {
    return subjectTeachers[subject] || null;
  };

  const handleTeacherEmailUpdate = async (e, subject) => {
    e.preventDefault();
    try {
      const rawToken = localStorage.getItem('token') || '';
      const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;
      
      // Determine if we're using email or code
      const usingCode = teacherInputMode[subject] === 'code';
      const teacherEmail = !usingCode ? subjectTeachers[subject] || '' : '';
      const code = usingCode ? teacherCode[subject] || '' : '';
      
      logger.debug(`Updating teacher for ${subject} using ${usingCode ? 'code' : 'email'}: ${usingCode ? code : teacherEmail}`);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/update-teacher`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Authorization': authToken,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            teacher_email: teacherEmail,
            teacher_code: code,
            subject: subject
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${await response.text()}`);
      }

      // After successful update, refresh the user profile data to ensure consistency
      try {
        const profileResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/profile`,
          {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Authorization': authToken,
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (profileResponse.ok) {
          const userData = await profileResponse.json();
          setUserData(userData);
          
          // Update the subject teachers from the refreshed data
          if (userData.teacher_email) {
            try {
              if (userData.teacher_email.startsWith('{')) {
                const teacherDict = JSON.parse(userData.teacher_email);
                setSubjectTeachers(teacherDict);
              } else {
                setSubjectTeachers({ "English": userData.teacher_email });
              }
            } catch (e) {
              logger.error('Error parsing refreshed teacher emails:', e);
            }
          }
        }
      } catch (profileError) {
        logger.error('Error refreshing profile after teacher update:', profileError);
        // We'll still show success even if refresh fails
      }

      // Reset the code inputs
      setTeacherCode(prev => ({
        ...prev,
        [subject]: ''
      }));

      setSuccess(`Teacher for ${subject} ${teacherEmail || code ? 'updated' : 'removed'} successfully`);
      setIsEditing(prev => ({
        ...prev,
        [subject]: false
      }));
      setError(null);
    } catch (error) {
      logger.error('Error updating teacher:', error);
      setError(error.message || 'Failed to update teacher connection');
    }
  };

  // Toggle between email and code input mode
  const toggleTeacherInputMode = (subject) => {
    setTeacherInputMode(prev => ({
      ...prev,
      [subject]: prev[subject] === 'code' ? 'email' : 'code'
    }));
  };

  // Update teacher code for a specific subject
  const updateTeacherCode = (subject, code) => {
    // Force uppercase and limit to 4 characters
    const formattedCode = code.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 4);
    
    setTeacherCode(prev => ({
      ...prev,
      [subject]: formattedCode
    }));
  };

  const handleRoleUpdate = async (selectedRole) => {
    try {
      const rawToken = localStorage.getItem('token') || '';
      const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/update-role`,
        {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: {
            'Authorization': authToken,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ role: selectedRole })
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${await response.text()}`);
      }

      const data = await response.json();
      setUserData({ ...userData, role: selectedRole, teacher_code: selectedRole === 'teacher' ? data.teacher_code : null });
      setSuccess('Role updated successfully');
      setIsSelectingRole(false);
      setError(null);
    } catch (error) {
      setError(error.message || 'Failed to update role');
    }
  };

  // Function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    }, (err) => {
      logger.error('Failed to copy text: ', err);
      setError('Failed to copy code');
    });
  };

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 20px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '15px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          ← Back
        </button>
        <h2 style={{ margin: 0 }}>My Account</h2>
      </div>

      <div style={{ 
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div style={{ marginBottom: '15px' }}>
          <strong>Username:</strong> {userData.username}
        </div>
        <div style={{ marginBottom: '15px' }}>
          <strong>Email:</strong> {userData.email}
        </div>
        <div style={{ marginBottom: '15px' }}>
          <strong>Role:</strong> {userData.role}
        </div>
        <div style={{ marginBottom: '15px' }}>
          <strong>Account Created:</strong> {new Date(userData.created_at).toLocaleDateString()}
        </div>
      </div>

      {/* Grammar Exemption Setting */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        marginBottom: '20px'
      }}>
        <h3>Accessibility Settings</h3>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <input
            type="checkbox"
            id="grammar-exemption"
            checked={userData.grammar_exemption || false}
            onChange={handleGrammarExemptionToggle}
            style={{ marginRight: '10px' }}
          />
          <label htmlFor="grammar-exemption">
            <strong>Grammar & Spelling Exemption</strong>
          </label>
        </div>
        <div style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginLeft: '25px',
          marginBottom: '15px'
        }}>
          Enable this option if you have spelling/grammar difficulties or dyslexia. When enabled, your English essays will be evaluated without considering spelling or grammar mistakes.
        </div>
      </div>

      {!userData.role && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: '20px'
        }}>
          <h3>Select Your Role</h3>

          {!isSelectingRole ? (
            <div>
              <p style={{ color: '#666', marginBottom: '15px' }}>
                Please select your role to access all features
              </p>
              <button
                onClick={() => setIsSelectingRole(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2980b9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Select Role
              </button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                  onClick={() => handleRoleUpdate('student')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  I'm a Student
                </button>
                <button
                  onClick={() => handleRoleUpdate('teacher')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  I'm a Teacher
                </button>
              </div>
              <button
                onClick={() => setIsSelectingRole(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {userData.role === 'teacher' && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: '20px'
        }}>
          <h3>Teacher Code</h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '15px',
            backgroundColor: '#e8f4f8',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '10px',
              textAlign: 'center' 
            }}>
              Share this code with your students so they can connect to you:
            </div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold',
              letterSpacing: '5px',
              color: '#2980b9',
              marginBottom: '10px'
            }}>
              {userData.teacher_code || 'Loading...'}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#666',
              textAlign: 'center' 
            }}>
              Students can use either this code or your email address ({userData.email}) to connect their accounts to yours.
            </div>
          </div>
        </div>
      )}

      {(userData.role === 'student' || !userData.role) && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: '20px'
        }}>
          <h3>Teacher Connections</h3>
          
          {subjects.map(subject => (
            <div key={subject} style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              borderBottom: '1px solid #eee',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px'
            }}>
              <h4 style={{ marginTop: 0 }}>{subject}</h4>
              {!isEditing[subject] ? (
                <div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '10px'
                  }}>
                    <p style={{ margin: 0 }}>
                      <strong>Teacher Email:</strong>{' '}
                      {getTeacherForSubject(subject) || 'No teacher connected'}
                    </p>
                    {getTeacherForSubject(subject) && (
                      <button
                        onClick={async () => {
                          try {
                            const rawToken = localStorage.getItem('token') || '';
                            const authToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;

                            // Make API call to remove teacher
                            const response = await fetch(
                              `${process.env.REACT_APP_API_BASE_URL}/api/update-teacher`,
                              {
                                method: 'POST',
                                mode: 'cors',
                                credentials: 'include',
                                headers: {
                                  'Authorization': authToken,
                                  'Accept': 'application/json',
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  subject: subject,
                                  teacher_email: '',
                                  teacher_code: ''
                                })
                              }
                            );

                            if (!response.ok) {
                              throw new Error('Failed to remove teacher');
                            }

                            // Update local state
                            const updatedTeachers = { ...subjectTeachers };
                            delete updatedTeachers[subject];
                            setSubjectTeachers(updatedTeachers);
                            
                            setSuccess(`Removed teacher for ${subject}`);
                            setError(null);
                          } catch (err) {
                            setError('Failed to remove teacher connection');
                            console.error(err);
                          }
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: 'transparent',
                          color: '#2980b9',
                          border: '1px solid #2980b9',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginLeft: '10px',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#e8f4f8'
                          }
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f4f8'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        title="Remove teacher connection"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleEditTeacher(subject)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2980b9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {getTeacherForSubject(subject) ? 'Change Teacher' : 'Add Teacher'}
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => handleTeacherEmailUpdate(e, subject)}>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      marginBottom: '15px' 
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        border: '1px solid #cbd5e1', 
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <button
                          type="button"
                          onClick={() => setTeacherInputMode(prev => ({ ...prev, [subject]: 'email' }))}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: teacherInputMode[subject] !== 'code' ? '#2980b9' : '#f8f9fa',
                            color: teacherInputMode[subject] !== 'code' ? 'white' : '#333',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          Use Email
                        </button>
                        <button
                          type="button"
                          onClick={() => setTeacherInputMode(prev => ({ ...prev, [subject]: 'code' }))}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: teacherInputMode[subject] === 'code' ? '#2980b9' : '#f8f9fa',
                            color: teacherInputMode[subject] === 'code' ? 'white' : '#333',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          Use Code
                        </button>
                      </div>
                    </div>
                    
                    {teacherInputMode[subject] === 'code' ? (
                      // Teacher code input
                      <div>
                        <input
                          type="text"
                          value={teacherCode[subject] || ''}
                          onChange={(e) => updateTeacherCode(subject, e.target.value)}
                          placeholder="Enter teacher's 4-digit code"
                          style={{
                            width: '100%',
                            padding: '8px',
                            marginBottom: '10px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            textAlign: 'center',
                            fontSize: '18px',
                            letterSpacing: '5px',
                            textTransform: 'uppercase'
                          }}
                          maxLength="4"
                        />
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '15px',
                          textAlign: 'center'
                        }}>
                          Enter the 4-digit code provided by your teacher
                        </div>
                      </div>
                    ) : (
                      // Teacher email input (default)
                      <div>
                        <input
                          type="email"
                          value={subjectTeachers[subject] || ''}
                          onChange={(e) => updateSubjectTeacher(subject, e.target.value)}
                          placeholder="Enter teacher's email"
                          style={{
                            width: '100%',
                            padding: '8px',
                            marginBottom: '10px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1'
                          }}
                        />
                        <div style={{
                          fontSize: '12px',
                          color: '#666',
                          marginBottom: '15px'
                        }}>
                          {getTeacherForSubject(subject) 
                            ? "Leave empty and click Save to remove this teacher connection" 
                            : `This will link your account to your ${subject} teacher's account`}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="submit"
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#2980b9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Save
                    </button>
                    {getTeacherForSubject(subject) && (
                      <button
                        type="button"
                        onClick={() => {
                          // Clear the teacher email and submit the form
                          updateSubjectTeacher(subject, '');
                          // Reset input mode to email for next time
                          setTeacherInputMode(prev => ({ ...prev, [subject]: 'email' }));
                          handleTeacherEmailUpdate({ preventDefault: () => {} }, subject);
                        }}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => cancelEditTeacher(subject)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          ))}

          {error && (
            <div style={{ color: '#e74c3c', marginTop: '10px' }}>{error}</div>
          )}
          {success && (
            <div style={{ color: '#27ae60', marginTop: '10px' }}>{success}</div>
          )}
        </div>
      )}

      {userData.role === 'teacher' && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: '20px'
        }}>
          <h3>My Students</h3>
          
          {students && Object.keys(students).length > 0 ? (
            <div>
              {/* Subject tabs */}
              <div style={{ 
                display: 'flex', 
                borderBottom: '1px solid #dee2e6', 
                marginBottom: '20px',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                paddingBottom: '10px'
              }}>
                {Object.keys(students)
                  // Sort subjects by number of students (descending)
                  .sort((a, b) => {
                    // Get counts
                    const countA = students[a]?.length || 0;
                    const countB = students[b]?.length || 0;
                    
                    // Sort by count (descending)
                    if (countA !== countB) {
                      return countB - countA;
                    }
                    
                    // If counts are equal, sort alphabetically
                    return a.localeCompare(b);
                  })
                  .map(subject => {
                    const studentCount = students[subject]?.length || 0;
                    // Skip rendering subjects with no students or render them differently
                    return (
                      <button
                        key={subject}
                        onClick={() => setSelectedSubject(subject)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: selectedSubject === subject ? '#2980b9' : '#f8f9fa',
                          color: selectedSubject === subject ? 'white' : studentCount > 0 ? '#333' : '#888',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '10px',
                          fontWeight: selectedSubject === subject ? 'bold' : 'normal',
                          // Make empty subjects smaller and less prominent
                          fontSize: studentCount > 0 ? '14px' : '12px',
                          opacity: studentCount > 0 ? 1 : 0.7
                        }}
                      >
                        {subject} ({studentCount})
                      </button>
                    );
                  })}
              </div>
              
              {/* Selected subject's students */}
              {students[selectedSubject]?.length > 0 ? (
                <div>
                  <h4>{selectedSubject} Students</h4>
                  {students[selectedSubject].map((student, index) => (
                    <div 
                      key={`${student.username}-${index}`}
                      style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '10px'
                      }}
                    >
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Username:</strong> {student.username}
                      </div>
                      {student.email && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>Email:</strong> {student.email}
                        </div>
                      )}
                      {student.school && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>School:</strong> {student.school}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#666' }}>
                  No {selectedSubject} students connected yet. Students can connect to you using your email address: {userData.email} or your teacher code: <strong>{userData.teacher_code}</strong>
                </p>
              )}
            </div>
          ) : (
            <p style={{ color: '#666' }}>
              No students connected yet. Students can connect to you using your email address: {userData.email} or your teacher code: <strong>{userData.teacher_code}</strong>
            </p>
          )}
        </div>
      )}

      {error && (
        <div style={{ color: '#e74c3c', marginTop: '10px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>{error}</div>
      )}
      {success && (
        <div style={{ color: '#27ae60', marginTop: '10px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>{success}</div>
      )}
    </div>
  );
};

export default MyAccount; 